import React, { useEffect, useState } from 'react';
import { Typography, Upload, Spin, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { IconPhotoPlus, IconX } from '@tabler/icons-react';
import { message } from '../../services/toastMessage';
import {
  deleteServiceRequestPhoto,
  getServiceRequestPhotoObjectUrl,
  uploadServiceRequestPhoto,
} from '../../services/ServiceRequestService';
import { IServiceRequestPhoto } from '../../interfaces/heavyDutyMachinery/IServiceRequest';
import { compressPhoto } from '../../helpers/compressPhoto';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';

const { Text } = Typography;

/** Four. Past that it stops being "mira cómo está esto" and becomes an album nobody opens. */
const MAX_PHOTOS = 4;

interface PhotoThumbProps {
  photo: IServiceRequestPhoto;
  onRemove?: () => void;
}

/**
 * One photo, fetched through the authenticated endpoint and held as a local
 * object URL -- same convention as the dispute evidence thumbnails, for the
 * same reason: it is never reachable from a guessable URL.
 */
const PhotoThumb: React.FC<PhotoThumbProps> = ({ photo, onRemove }) => {
  const { token } = theme.useToken();
  const [url, setUrl] = useState<string>();
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let objectUrl: string | undefined;
    let cancelled = false;
    getServiceRequestPhotoObjectUrl(photo.id)
      .then((result) => {
        objectUrl = result;
        if (cancelled) {
          window.URL.revokeObjectURL(result);
          return;
        }
        setUrl(result);
      })
      .catch(() => setFailed(true));
    return () => {
      cancelled = true;
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
  }, [photo.id]);

  return (
    <div
      style={{
        position: 'relative',
        width: 84,
        height: 84,
        borderRadius: 12,
        overflow: 'hidden',
        background: token.colorFillQuaternary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {url ? (
        <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : failed ? (
        <IconX size={16} style={{ color: token.colorTextTertiary }} />
      ) : (
        <Spin size="small" />
      )}

      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label="x"
          style={{
            position: 'absolute',
            top: 4,
            right: 4,
            width: 22,
            height: 22,
            borderRadius: '50%',
            border: 'none',
            background: 'rgba(0,0,0,0.55)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <IconX size={13} />
        </button>
      )}
    </div>
  );
};

interface JobPhotosProps {
  photos: IServiceRequestPhoto[];
  onChange: (photos: IServiceRequestPhoto[]) => void;
}

/**
 * "Hazme una foto de eso."
 *
 * The highest-information thing anybody can send here: it settles in one tap
 * what four well-chosen questions circle around. And it is the one input a
 * customer can always produce correctly -- they may not know what the pipe is
 * made of, but they can point a phone at it.
 *
 * Uploaded as they are picked, not held until send: on a phone connection
 * four photos at the end of a form is a progress bar somebody abandons. They
 * hang unattached server-side until the request is sent, and a sweep clears
 * whatever never was.
 */
const JobPhotos: React.FC<JobPhotosProps> = ({ photos, onChange }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (file: File) => {
    if (photos.length >= MAX_PHOTOS) {
      message.error(translate(t, TRANSLATIONS.JOB_PHOTOS_MAX, { max: MAX_PHOTOS }));
      return;
    }

    setUploading(true);
    try {
      // Compressed in the browser first, the same way every other photo in
      // this app is: these come off a phone camera at several megabytes, over
      // whatever signal there is in somebody's bathroom.
      const uploaded = await uploadServiceRequestPhoto(await compressPhoto(file));
      onChange([...photos, uploaded]);
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.JOB_PHOTOS_ERROR));
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async (photo: IServiceRequestPhoto) => {
    // Optimistic: the row is the client's own and still unattached, so the
    // only way this fails is a network blip -- and leaving a thumbnail on
    // screen that the customer just dismissed is the worse outcome. The sweep
    // clears anything the server kept.
    onChange(photos.filter((p) => p.id !== photo.id));
    try {
      await deleteServiceRequestPhoto(photo.id);
    } catch {
      // Nothing to say: it is already gone from their screen.
    }
  };

  return (
    <div style={{ marginBottom: 20 }}>
      <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 4 }}>
        {translate(t, TRANSLATIONS.JOB_PHOTOS_LABEL)}
      </Text>
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 10 }}>
        {translate(t, TRANSLATIONS.JOB_PHOTOS_HINT)}
      </Text>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {photos.map((photo) => (
          <PhotoThumb key={photo.id} photo={photo} onRemove={() => handleRemove(photo)} />
        ))}

        {photos.length < MAX_PHOTOS && (
          <Upload
            accept="image/*"
            showUploadList={false}
            beforeUpload={(file) => {
              handleUpload(file as File);
              return false;
            }}
          >
            <div
              style={{
                width: 84,
                height: 84,
                borderRadius: 12,
                border: `1px dashed ${token.colorBorder}`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
                cursor: 'pointer',
                color: token.colorTextTertiary,
              }}
            >
              {uploading ? <Spin size="small" /> : <IconPhotoPlus size={20} />}
              <Text type="secondary" style={{ fontSize: 10 }}>
                {translate(t, TRANSLATIONS.JOB_PHOTOS_ADD)}
              </Text>
            </div>
          </Upload>
        )}
      </div>
    </div>
  );
};

export default JobPhotos;
export { PhotoThumb };
